(() => {
  'use strict';
  if (window.__lldgProjectIndexLoaded) {
    document.dispatchEvent(new CustomEvent('lldg:project-index:refresh'));
    return;
  }
  window.__lldgProjectIndexLoaded = true;
  const selector = '[data-project-library]';
  const instances = new Map();
  let nextInstanceId = 0;
  let activeNativeTransition = null;

  class ProjectIndex {
    constructor(root) {
      this.root = root;
      this.events = new AbortController();
      this.itemAnimations = [];
      this.namedElements = [];
      this.renderRevision = 0;
      this.instanceId = ++nextInstanceId;
      this.grid = root.querySelector('[data-project-grid]');
      this.index = root.querySelector('[data-project-index]');
      if (!this.grid || !this.index) return;
      this.cards = Array.from(this.grid.querySelectorAll('[data-project-item]'));
      this.rows = Array.from(this.index.querySelectorAll('[data-project-item]'));
      this.status = root.querySelector('[data-project-results-status]');
      this.empty = root.querySelector('[data-project-filter-empty]');
      this.sortSelect = root.querySelector('[data-project-sort]');
      this.view = root.dataset.defaultView === 'list' ? 'list' : 'grid';
      this.category = '';
      this.sort = Array.from(this.sortSelect?.options || []).some(option => option.value === root.dataset.defaultSort) ? root.dataset.defaultSort : 'native';
      if (this.sortSelect) this.sortSelect.value = this.sort;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.films = [];
      this.transitionTimer = null;
      this.revealObserver = null;
      this.snapshotCleanup = null;
      this.buildFilters();
      this.setupFilms();
      this.setupReveals();
      this.setupViewTransitions();
      const listener = { signal: this.events.signal };
      root.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        const view = target?.closest('[data-project-view-button]');
        const filter = target?.closest('[data-project-filter]');
        const film = target?.closest('[data-project-film-toggle]');
        if (view && root.contains(view)) {
          const nextView = view.dataset.projectViewButton === 'list' ? 'list' : 'grid';
          if (this.view === nextView) return;
          this.view = nextView;
          this.render(true);
        } else if (filter && root.contains(filter)) {
          if (this.category === filter.dataset.projectFilter) return;
          this.category = filter.dataset.projectFilter;
          this.render(true);
        } else if (film && root.contains(film)) {
          const entry = this.films.find(item => item.button === film);
          if (entry) {
            entry.manuallyPaused = !entry.video.paused;
            entry.userStarted = entry.video.paused;
            if (entry.video.paused) this.playFilm(entry);
            else entry.video.pause();
          }
        }
      }, listener);
      this.sortSelect?.addEventListener('change', () => { this.sort = this.sortSelect.value; this.render(true); }, listener);
      this.motion.addEventListener('change', () => {
        this.root.dataset.projectMotion = String(this.motionAllowed());
        if (this.motion.matches) {
          this.render();
          this.films.forEach(entry => entry.video.pause());
        } else this.updateFilms();
      }, listener);
      document.addEventListener('visibilitychange', () => this.updateFilms(), listener);
      this.root.dataset.projectEnhanced = 'true';
      this.root.dataset.projectMotion = String(this.motionAllowed());
      this.render();
      this.renderedView = this.view;
      root.querySelectorAll('[data-project-toolbar]').forEach(toolbar => { toolbar.hidden = false; });
    }

    buildFilters() {
      const group = this.root.querySelector('[data-project-category-filters]');
      const all = group?.querySelector('[data-project-filter]');
      if (!group || !all) return;
      const categories = new Map([['Residential', 0], ['Commercial', 0]]);
      this.cards.forEach(card => {
        const category = card.dataset.projectCategory.trim();
        if (categories.has(category)) categories.set(category, categories.get(category) + 1);
      });
      categories.forEach((count, category) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = all.className;
        button.dataset.projectFilter = category;
        button.dataset.projectGeneratedFilter = '';
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-controls', all.getAttribute('aria-controls'));
        const label = document.createElement('span');
        label.textContent = category;
        const total = document.createElement('span');
        total.className = 'project-library__filter-count';
        total.textContent = String(count);
        button.append(label, document.createTextNode(' '), total);
        group.append(button);
      });
    }

    ordered(items) {
      return items.slice().sort((a, b) => {
        const fallback = Number(a.dataset.projectOrder) - Number(b.dataset.projectOrder);
        if (this.sort === 'native') return fallback;
        const yearA = a.dataset.projectYear.trim() === '' ? null : Number(a.dataset.projectYear);
        const yearB = b.dataset.projectYear.trim() === '' ? null : Number(b.dataset.projectYear);
        const validA = yearA !== null && Number.isFinite(yearA);
        const validB = yearB !== null && Number.isFinite(yearB);
        if (validA !== validB) return validA ? -1 : 1;
        if (!validA) return fallback;
        return (this.sort === 'year-asc' ? yearA - yearB : yearB - yearA) || fallback;
      });
    }

    render(animate = false) {
      if (this.destroyed || !this.grid || !this.index) return;
      const revision = ++this.renderRevision;
      this.stopTransition();
      const previousView = this.renderedView ?? this.view;
      const shouldAnimate = animate && this.motionAllowed();
      if (shouldAnimate && typeof document.startViewTransition === 'function') {
        // A document can have only one native transition. Finish its visual layer
        // before starting another, including changes in another library section.
        activeNativeTransition?.owner.stopTransition();
        this.root.dataset.projectTransitioning = 'true';
        document.documentElement.dataset.projectViewTransition = 'true';
        try {
          const transition = document.startViewTransition(() => {
            if (!this.destroyed && revision === this.renderRevision) this.applyState(true);
          });
          this.viewTransition = transition;
          activeNativeTransition = { owner: this, transition };
          const finish = () => {
            if (this.viewTransition !== transition) return;
            this.viewTransition = null;
            if (activeNativeTransition?.transition === transition) {
              activeNativeTransition = null;
              delete document.documentElement.dataset.projectViewTransition;
            }
            delete this.root.dataset.projectTransitioning;
          };
          // A skipped transition rejects ready, while its update still completes.
          transition.ready?.catch(() => {});
          transition.finished.then(finish, finish);
          return;
        } catch (error) {
          this.viewTransition = null;
          delete document.documentElement.dataset.projectViewTransition;
          delete this.root.dataset.projectTransitioning;
        }
      }
      const beforeLayout = shouldAnimate && previousView === this.view
        ? this.captureLayout(previousView === 'grid' ? this.cards : this.rows)
        : null;
      const snapshot = shouldAnimate && previousView !== this.view
        ? this.captureView(previousView === 'grid' ? this.grid : this.index)
        : null;
      if (shouldAnimate) this.root.dataset.projectTransitioning = 'true';
      this.applyState(shouldAnimate);
      const activeView = this.view === 'grid' ? this.grid : this.index;
      if (shouldAnimate && typeof activeView.animate === 'function') {
        if (snapshot) this.animateViewChange(snapshot, activeView);
        else if (beforeLayout) this.animateLayout(beforeLayout, this.view === 'grid' ? this.cards : this.rows);
        this.transitionTimer = window.setTimeout(() => this.stopTransition(), 600);
      } else {
        this.stopTransition();
      }
    }

    applyState(reveal = false) {
      const cards = this.ordered(this.cards);
      const rows = this.ordered(this.rows);
      cards.forEach(card => { card.hidden = this.category !== '' && card.dataset.projectCategory !== this.category; });
      rows.forEach(row => { row.hidden = this.category !== '' && row.dataset.projectCategory !== this.category; });
      if (cards.some((card, index) => this.grid.children[index] !== card)) this.grid.append(...cards);
      const tbody = this.index.querySelector('tbody');
      if (rows.some((row, index) => tbody.children[index] !== row)) tbody.append(...rows);
      const visible = cards.filter(card => !card.hidden);
      this.layoutCards(cards, visible);
      this.grid.dataset.visibleCount = String(visible.length);
      this.grid.hidden = this.view !== 'grid';
      this.index.hidden = this.view !== 'list';
      this.root.dataset.projectView = this.view;
      this.root.querySelectorAll('[data-project-view-button]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.projectViewButton === this.view)));
      this.root.querySelectorAll('[data-project-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.projectFilter === this.category)));
      if (this.empty) this.empty.hidden = visible.length > 0;
      const noun = visible.length === 1 ? this.root.dataset.statusSingular : this.root.dataset.statusPlural;
      if (this.status) this.status.textContent = `${visible.length} ${noun} ${this.root.dataset.statusScope}`;
      this.updateFilms();
      this.renderedView = this.view;
      if (reveal) {
        const items = this.view === 'grid' ? cards : rows;
        items.filter(item => !item.hidden).forEach(item => {
          item.dataset.projectRevealed = '';
          this.revealObserver?.unobserve(item);
        });
      }
    }

    layoutCards(cards, visible) {
      cards.forEach(card => {
        card.classList.remove('project-library-card--single');
        card.style.removeProperty('--project-card-fraction');
        card.style.removeProperty('--project-row-gaps');
      });
      if (visible.length % 2 === 1) visible.at(-1).classList.add('project-library-card--single');
      if (!this.grid.classList.contains('project-library__grid--editorial')) return;
      // Work in visible order so category and year changes recompose the rows
      // inside the same native transition or FLIP update as the cards themselves.
      const patterns = [[2.19, 1], [2.2, .75, 1.2], [1.6, 2, .85]];
      let offset = 0;
      let row = 0;
      while (offset < visible.length) {
        const remaining = visible.length - offset;
        let weights = patterns[row % patterns.length];
        let count = Math.min(weights.length, remaining);
        // Keep a final project with its neighbors instead of stretching one
        // landscape image across a whole extra row after filtering.
        if (remaining === 3 && count === 2) {
          count = 3;
          weights = patterns[1];
        } else if (remaining === 4 && count === 3) count = 2;
        weights = weights.slice(0, count);
        const total = weights.reduce((sum, weight) => sum + weight, 0);
        visible.slice(offset, offset + count).forEach((card, index) => {
          card.style.setProperty('--project-card-fraction', String(weights[index] / total));
          card.style.setProperty('--project-row-gaps', String(count - 1));
        });
        offset += count;
        row++;
      }
    }

    setupFilms() {
      this.cards.forEach(card => {
        const video = card.querySelector('[data-project-film] video');
        const button = card.querySelector('[data-project-film-toggle]');
        if (!video || !button) return;
        const entry = { card, video, button, inView: false, manuallyPaused: false, userStarted: false };
        this.films.push(entry);
        video.muted = true;
        video.playsInline = true;
        button.hidden = false;
        const reflectPlayback = () => {
          const playing = !video.paused;
          card.toggleAttribute('data-film-playing', playing);
          button.setAttribute('aria-pressed', String(playing));
          const text = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
          button.querySelector('[data-film-label]').textContent = text;
          button.setAttribute('aria-label', `${text}: ${button.dataset.filmTitle}`);
        };
        video.addEventListener('playing', reflectPlayback, { signal: this.events.signal });
        video.addEventListener('pause', reflectPlayback, { signal: this.events.signal });
        video.addEventListener('error', () => { video.pause(); button.hidden = true; }, { signal: this.events.signal });
      });
      if (typeof IntersectionObserver === 'function' && this.films.length) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(observation => {
            const entry = this.films.find(item => item.card === observation.target);
            if (entry) entry.inView = observation.isIntersecting;
          });
          this.updateFilms();
        }, { threshold: .15 });
        this.films.forEach(entry => this.observer.observe(entry.card));
      } else if (this.films.length) {
        const checkVisibility = () => {
          this.films.forEach(entry => {
            const bounds = entry.card.getBoundingClientRect();
            entry.inView = bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth;
          });
          this.updateFilms();
        };
        const listener = { passive: true, signal: this.events.signal };
        window.addEventListener('scroll', checkVisibility, listener);
        window.addEventListener('resize', checkVisibility, listener);
        checkVisibility();
      }
    }

    playFilm(entry) {
      const request = entry.video.play();
      request?.catch(() => { entry.userStarted = false; });
    }

    motionAllowed() {
      return !this.motion.matches && document.documentElement.dataset.motionEnabled !== 'false' && !window.Shopify?.designMode;
    }

    setupViewTransitions() {
      const assignName = (element, name, transitionClass) => {
        if (!element) return;
        this.namedElements.push({ element, name: element.style.getPropertyValue('view-transition-name'), transitionClass: element.style.getPropertyValue('view-transition-class') });
        element.style.setProperty('view-transition-name', name);
        element.style.setProperty('view-transition-class', transitionClass);
      };
      this.cards.forEach(card => {
        const key = `${this.instanceId}-${card.dataset.projectOrder}`;
        assignName(card.querySelector('.project-library-card__media'), `lldg-project-image-${key}`, 'project-library-image');
        assignName(card.querySelector('.project-library-card__body'), `lldg-project-info-${key}`, 'project-library-info');
      });
      this.rows.forEach(row => {
        const key = `${this.instanceId}-${row.dataset.projectOrder}`;
        assignName(row.querySelector('.project-library__table-link'), `lldg-project-info-${key}`, 'project-library-info');
      });
    }

    captureLayout(items) {
      return new Map(items.filter(item => !item.hidden).map(item => {
        const bounds = item.getBoundingClientRect();
        return [item.dataset.projectOrder, { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height }];
      }));
    }

    animateLayout(before, items) {
      items.filter(item => !item.hidden).forEach(item => {
        const start = before.get(item.dataset.projectOrder);
        const end = item.getBoundingClientRect();
        const frames = start ? [
          { transformOrigin: 'top left', transform: `translate(${start.left - end.left}px, ${start.top - end.top}px) scale(${end.width ? start.width / end.width : 1}, ${end.height ? start.height / end.height : 1})` },
          { transformOrigin: 'top left', transform: 'none' }
        ] : [{ opacity: 0 }, { opacity: 1 }];
        this.itemAnimations.push(item.animate(frames, {
          duration: start ? 600 : 400,
          easing: start ? 'cubic-bezier(.76, 0, .24, 1)' : 'cubic-bezier(.16, 1, .3, 1)',
          fill: 'both'
        }));
      });
    }

    captureView(element) {
      if (typeof element.animate !== 'function') return null;
      // Capture before changing hidden state. The snapshot is visual only, so the
      // outgoing view cannot remain focusable or become visible on interruption.
      const bounds = element.getBoundingClientRect();
      const parentBounds = this.root.getBoundingClientRect();
      const snapshot = element.cloneNode(true);
      snapshot.dataset.projectSnapshot = '';
      snapshot.setAttribute('aria-hidden', 'true');
      snapshot.setAttribute('inert', '');
      [snapshot, ...snapshot.querySelectorAll('*')].forEach(node => {
        node.removeAttribute('id');
        node.style.setProperty('view-transition-name', 'none');
      });
      Object.assign(snapshot.style, {
        position: 'absolute', margin: '0',
        top: `${bounds.top - parentBounds.top + this.root.scrollTop}px`,
        left: `${bounds.left - parentBounds.left + this.root.scrollLeft}px`,
        width: `${bounds.width}px`, pointerEvents: 'none', zIndex: '1'
      });
      return snapshot;
    }

    animateViewChange(snapshot, nextElement) {
      const originalPosition = this.root.style.position;
      this.root.style.position = 'relative';
      this.root.append(snapshot);
      this.snapshotCleanup = () => {
        snapshot.remove();
        this.root.style.position = originalPosition;
        this.snapshotCleanup = null;
      };
      const timing = { duration: 400, easing: 'cubic-bezier(.16, 1, .3, 1)', fill: 'both' };
      this.itemAnimations.push(
        snapshot.animate([{ opacity: 1 }, { opacity: 0 }], timing),
        nextElement.animate([{ opacity: 0 }, { opacity: 1 }], timing)
      );
    }

    setupReveals() {
      const items = [...this.cards, ...this.rows];
      items.forEach((item, index) => {
        item.dataset.projectReveal = '';
        item.style.setProperty('--project-reveal-delay', `${Math.min(index * 70, 420)}ms`);
      });
      if (typeof IntersectionObserver !== 'function') {
        items.forEach(item => { item.dataset.projectRevealed = ''; });
        return;
      }
      this.revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.dataset.projectRevealed = '';
          this.revealObserver?.unobserve(entry.target);
        });
      }, { threshold: .08, rootMargin: '0px 0px -8% 0px' });
      items.forEach(item => this.revealObserver.observe(item));
    }

    stopTransition() {
      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
      if (this.viewTransition) {
        this.viewTransition.skipTransition();
        this.viewTransition = null;
        if (activeNativeTransition?.owner === this) {
          activeNativeTransition = null;
          delete document.documentElement.dataset.projectViewTransition;
        }
      }
      this.itemAnimations.forEach(animation => animation.cancel());
      this.itemAnimations = [];
      this.snapshotCleanup?.();
      delete this.root.dataset.projectTransitioning;
    }

    updateFilms() {
      if (this.destroyed) return;
      // The visibility fallback supports manual films only; autoplay requires an observer.
      const canAutoplay = !!this.observer && this.root.dataset.filmAutoplay === 'true' && this.motionAllowed() && !navigator.connection?.saveData;
      this.films.forEach(entry => {
        const visible = !document.hidden && !entry.card.hidden && this.view === 'grid' && entry.inView;
        if (!visible) entry.video.pause();
        else if (entry.inView && canAutoplay && !entry.manuallyPaused && entry.video.paused) this.playFilm(entry);
      });
    }

    destroy() {
      this.destroyed = true;
      this.events.abort();
      this.observer?.disconnect();
      this.revealObserver?.disconnect();
      this.renderRevision++;
      this.stopTransition();
      this.films?.forEach(entry => { entry.video.pause(); entry.button.hidden = true; entry.card.removeAttribute('data-film-playing'); });
      if (!this.grid || !this.index) return;
      this.root.querySelectorAll('[data-project-generated-filter]').forEach(button => button.remove());
      this.root.querySelectorAll('[data-project-item]').forEach(item => {
        delete item.dataset.projectReveal;
        delete item.dataset.projectRevealed;
        item.style.removeProperty('--project-reveal-delay');
      });
      this.namedElements.forEach(({ element, name, transitionClass }) => {
        if (name) element.style.setProperty('view-transition-name', name);
        else element.style.removeProperty('view-transition-name');
        if (transitionClass) element.style.setProperty('view-transition-class', transitionClass);
        else element.style.removeProperty('view-transition-class');
      });
      this.category = '';
      this.sort = 'native';
      this.view = 'grid';
      this.applyState();
      this.root.querySelectorAll('[data-project-toolbar]').forEach(toolbar => { toolbar.hidden = true; });
      delete this.root.dataset.projectEnhanced;
      delete this.root.dataset.projectView;
      delete this.root.dataset.projectMotion;
    }
  }

  function initialize(scope = document) {
    instances.forEach((instance, root) => {
      if (!root.isConnected) { instance.destroy(); instances.delete(root); }
    });
    const roots = [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new ProjectIndex(root)); });
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (event.target === root || event.target.contains(root)) { instance.destroy(); instances.delete(root); }
    });
  });
  document.addEventListener('lldg:project-index:refresh', () => initialize());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
